/**
 * @fileoverview i18n Configuration and Setup
 * 
 * Central configuration for internationalization system.
 * Sets English as default language with extensibility for future languages.
 * 
 * @module i18n
 * @version 1.0.0
 * @since 2025-07-09
 */

// Import des types depuis le fichier types.ts (doit être en haut)
import type { TranslationKey } from './locales/types';

// Types pour la configuration i18n
export interface I18nConfig {
  defaultLocale: string;
  availableLocales: string[];
  fallbackLocale: string;
  resources: Record<string, any>;
}

// Langues disponibles
export const AVAILABLE_LOCALES = ['en', 'fr', 'de'] as const;
export type Locale = typeof AVAILABLE_LOCALES[number];

// Configuration par défaut
export const DEFAULT_LOCALE: Locale = 'en';
export const FALLBACK_LOCALE: Locale = 'en';

// Interface pour le contexte i18n
export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, params?: Record<string, any>) => string;
  isLoading: boolean;
  error: string | null;
}

// Configuration initiale
export const i18nConfig: I18nConfig = {
  defaultLocale: DEFAULT_LOCALE,
  availableLocales: [...AVAILABLE_LOCALES],
  fallbackLocale: FALLBACK_LOCALE,
  resources: {} // Sera chargé dynamiquement
};

// Utilitaires de validation
export const isValidLocale = (locale: string): locale is Locale => {
  return AVAILABLE_LOCALES.includes(locale as Locale);
};

// Détection de la langue du navigateur
export const detectBrowserLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  
  const browserLang = navigator.language.split('-')[0];
  return isValidLocale(browserLang) ? browserLang : DEFAULT_LOCALE;
};

// Utilitaire pour charger les traductions
export const loadLocaleMessages = async (locale: Locale): Promise<Record<string, any>> => {
  try {
    // Import dynamique des fichiers de traduction
    const messages = await import(`./locales/${locale}.json`);
    return messages.default || messages;
  } catch (error) {
    console.warn(`Failed to load locale "${locale}", falling back to ${FALLBACK_LOCALE}`);
    
    if (locale !== FALLBACK_LOCALE) {
      return loadLocaleMessages(FALLBACK_LOCALE);
    }
    
    // Si même le fallback échoue, retourner un objet vide
    return {};
  }
};

// Export des types pour utilisation dans l'app
export type { TranslationKey };